// Minimal JMAP client for Stalwart (RFC 8620 / 8621).
//
// Covers what the webmail needs: session discovery, mailbox list, email query +
// get, and email submission. Deliberately small — not a full JMAP SDK.
//
// Auth: HTTP Basic (email + password) against Stalwart. In production this rides
// the same origin behind the PQC proxy, so credentials never cross a third party.

const CORE = 'urn:ietf:params:jmap:core';
const MAIL = 'urn:ietf:params:jmap:mail';
const SUBMISSION = 'urn:ietf:params:jmap:submission';

export class JmapClient {
  constructor({ baseUrl = '', authHeader = null } = {}) {
    this.baseUrl = baseUrl;          // '' => same-origin (proxied)
    this.authHeader = authHeader;    // 'Basic ...'
    this.session = null;
  }

  static basicAuth(email, password) {
    return 'Basic ' + btoa(`${email}:${password}`);
  }

  async connect() {
    // /.well-known/jmap 307-redirects to /jmap/session; hitting it directly
    // avoids the redirect dropping the Authorization header.
    const res = await fetch(`${this.baseUrl}/jmap/session`, {
      headers: { Authorization: this.authHeader },
    });
    if (!res.ok) throw new JmapError(`session failed: ${res.status}`, res.status);
    this.session = await res.json();
    this.accountId = this.session.primaryAccounts?.[MAIL]
      || Object.keys(this.session.accounts || {})[0];
    return this.session;
  }

  async request(methodCalls, using = [CORE, MAIL]) {
    // Stalwart advertises apiUrl using its internal container hostname
    // (https://<id>/jmap/); the browser can't resolve that. Force the
    // same-origin proxied path instead.
    const apiUrl = `${this.baseUrl}/jmap`;
    const res = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        Authorization: this.authHeader,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ using, methodCalls }),
    });
    if (!res.ok) throw new JmapError(`request failed: ${res.status}`, res.status);
    const body = await res.json();
    return body.methodResponses;
  }

  async listMailboxes() {
    const [resp] = await this.request([
      ['Mailbox/get', { accountId: this.accountId, ids: null }, '0'],
    ]);
    return resp[1].list;
  }

  async listEmails(mailboxId, limit = 50) {
    const responses = await this.request([
      ['Email/query', {
        accountId: this.accountId,
        filter: { inMailbox: mailboxId },
        sort: [{ property: 'receivedAt', isAscending: false }],
        limit,
      }, '0'],
      ['Email/get', {
        accountId: this.accountId,
        '#ids': { resultOf: '0', name: 'Email/query', path: '/ids' },
        properties: ['id', 'subject', 'from', 'to', 'receivedAt', 'preview',
          'hasAttachment', 'keywords'],
      }, '1'],
    ]);
    return responses[1][1].list;
  }

  async getEmailBody(emailId) {
    const [resp] = await this.request([
      ['Email/get', {
        accountId: this.accountId,
        ids: [emailId],
        properties: ['id', 'subject', 'from', 'to', 'receivedAt',
          'bodyValues', 'textBody', 'htmlBody', 'attachments'],
        fetchAllBodyValues: true,
      }, '0'],
    ]);
    return resp[1].list[0];
  }

  // Find mailbox ids by role (drafts, sent) for submission.
  async mailboxIdsByRole() {
    const boxes = await this.listMailboxes();
    const byRole = {};
    for (const b of boxes) if (b.role) byRole[b.role] = b.id;
    return byRole;
  }

  /**
   * Send a message via JMAP: create the email in Drafts (Email/set), then
   * EmailSubmission/set to hand it to the MTA, moving it to Sent on success.
   * @param {{from, to, subject, textBody}} msg
   */
  async getIdentityId(email) {
    const [resp] = await this.request([
      ['Identity/get', { accountId: this.accountId }, '0'],
    ], [CORE, SUBMISSION]);
    const list = resp[1].list || [];
    const match = list.find((i) => i.email === email) || list[0];
    return match?.id;
  }

  async send({ from, to, subject, textBody }) {
    const roles = await this.mailboxIdsByRole();
    const draftsId = roles.drafts || roles.inbox;
    const sentId = roles.sent || null;
    const identityId = await this.getIdentityId(from);

    const toList = (Array.isArray(to) ? to : String(to).split(','))
      .map((s) => s.trim()).filter(Boolean)
      .map((email) => ({ email }));

    const responses = await this.request([
      // 1. create the draft email
      ['Email/set', {
        accountId: this.accountId,
        create: {
          draft: {
            mailboxIds: { [draftsId]: true },
            keywords: { $draft: true },
            from: [{ email: from }],
            to: toList,
            subject: subject || '',
            bodyStructure: { type: 'text/plain', partId: 'b1' },
            bodyValues: { b1: { value: textBody || '' } },
          },
        },
      }, '0'],
      // 2. submit it (references the created email id)
      ['EmailSubmission/set', {
        accountId: this.accountId,
        onSuccessUpdateEmail: sentId ? {
          '#sendIt': { mailboxIds: { [sentId]: true }, 'keywords/$draft': null },
        } : undefined,
        create: {
          sendIt: {
            emailId: '#draft',
            identityId,
            envelope: {
              mailFrom: { email: from },
              rcptTo: toList.map((r) => ({ email: r.email })),
            },
          },
        },
      }, '1'],
    ], [CORE, MAIL, SUBMISSION]);

    const subRes = responses.find((r) => r[0] === 'EmailSubmission/set');
    const created = subRes?.[1]?.created?.sendIt;
    const notCreated = subRes?.[1]?.notCreated?.sendIt;
    if (notCreated) throw new JmapError('send failed: ' + JSON.stringify(notCreated));
    return created;
  }
}

export class JmapError extends Error {
  constructor(msg, status) { super(msg); this.name = 'JmapError'; this.status = status; }
}
